/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import Dates from 'utils/Dates'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'

const MergeAuditLogPanel: React.FC = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const entries = useTypedSelector(
        (state) => state.crmState?.mergeAuditLog ?? []
    )

    useEffect(() => {
        void dispatch(CompaniesAction.fetchMergeAuditLog())
    }, [dispatch])

    if (entries.length === 0) {
        return null
    }

    return (
        <div className={clsx('MergeAuditLogPanel rounded-xl border border-gray-200 p-4')}>
            <div className={clsx('flex items-center justify-between gap-3 mb-2')}>
                <div className={clsx('text-sm font-semibold')}>{t('Recent merge activity')}</div>
                <UiButton
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Secondary}
                    title={t('Download report') as string}
                    onClick={() => {
                        const blob = new Blob([JSON.stringify(entries, null, 2)], {
                            type: 'application/json;charset=utf-8',
                        })
                        const url = URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = 'CRM_Merge_Report.json'
                        link.click()
                        URL.revokeObjectURL(url)
                    }}
                />
            </div>
            <div className="mb-3 text-xs text-gray-600 dark:text-gray-300">
                {t('Merge history is stored on the server for audit and rollback support.')}
            </div>
            <div className={clsx('space-y-2')}>
                {entries.slice(0, 5).map((entry) => (
                    <div key={entry.id} className={clsx('text-sm text-gray-700')}>
                        <div>
                            {t('Merged into {{name}}', {
                                name:
                                    entry.mainCompanyName ||
                                    `${t('Company')} #${entry.mainCompanyId}`,
                            })}
                        </div>
                        <div className={clsx('text-xs text-gray-500')}>
                            {Dates.date(entry.createdAt)} ·{' '}
                            {entry.mergedCompanyIds.length} {t('merged')}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default MergeAuditLogPanel
